/*
 * Consent popup — show, hide, validate, post. Nothing else.
 *
 * Deliberately thin. assets/*.js has no test harness in this repo and a dead
 * feature has shipped past a green suite on exactly that gap, so anything
 * that could live server-side does: what counts as a valid signup, Shopify
 * customer creation, and the Klaviyo/WhatsApp fan-out are all the signup
 * endpoint's problem, not this file's.
 *
 * Two VISIBLE steps, ONE request. Step 1 (email + email opt-in) is a pure
 * client-side gate — no network call. Step 2 (phone, SMS/WhatsApp, the bot
 * check) is where the single combined POST actually happens, carrying every
 * field from both steps together. The endpoint's contract hasn't changed;
 * only when each field is shown to the visitor has.
 */
(function () {
  'use strict';

  var root = document.getElementById('ConsentPopup');
  if (!root) return;

  /*
   * ⛔ Trade suppression happens HERE, not in Liquid. The b2b_tier cookie holds the
   *    price-list tags, so it also catches a returning trade buyer who is browsing
   *    logged OUT — the case the `customer` object cannot see. Trade accounts are on
   *    tier pricing; a retail welcome offer either will not apply to them or, worse,
   *    stacks below cost.
   */
  if (document.cookie.indexOf('b2b_tier=') >= 0) return;

  var STORE_KEY = 'hdsConsentPopup';
  var endpoint = root.getAttribute('data-endpoint') || '/apps/signup';
  var consentTextVersion = root.getAttribute('data-consent-text-version') || 'popup-v1';
  var turnstileSitekey = root.getAttribute('data-turnstile-sitekey');
  var recurDays = parseInt(root.getAttribute('data-recur-days'), 10) || 30;
  var delayMs = (parseInt(root.getAttribute('data-delay'), 10) || 10) * 1000;

  function state() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
  }

  function save(next) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(next)); } catch (e) { /* private mode */ }
  }

  var stored = state();
  if (stored.joined) return;
  if (stored.closedAt && Date.now() - stored.closedAt < recurDays * 86400000) return;

  function step(name) {
    var panels = root.querySelectorAll('[data-consent-step]');
    for (var i = 0; i < panels.length; i++) {
      panels[i].hidden = panels[i].getAttribute('data-consent-step') !== name;
    }
  }

  function showError(message) {
    var el = root.querySelector('[data-consent-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function showEmailError(message) {
    var el = root.querySelector('[data-consent-error-email]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function busy(on) {
    var button = root.querySelector('[data-consent-submit]');
    if (button) button.disabled = !!on;
  }

  function close() {
    root.hidden = true;
    var next = state();
    next.closedAt = Date.now();
    save(next);
  }

  var closers = root.querySelectorAll('[data-consent-close]');
  for (var i = 0; i < closers.length; i++) closers[i].addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !root.hidden) close();
  });

  /*
   * SMS/WhatsApp are only tickable once a phone number is present — per the
   * spec, a channel a customer never gave a number for can't record consent
   * for it. Emptying the field un-ticks and re-disables both; it must never
   * be possible to submit sms/whatsapp true with no phone.
   */
  var phoneInput = root.querySelector('#ConsentPhone');
  var smsCheck = root.querySelector('[data-consent-sms]');
  var whatsappCheck = root.querySelector('[data-consent-whatsapp]');

  function syncPhoneGate() {
    var hasPhone = !!(phoneInput.value || '').trim();
    smsCheck.disabled = !hasPhone;
    whatsappCheck.disabled = !hasPhone;
    if (!hasPhone) {
      smsCheck.checked = false;
      whatsappCheck.checked = false;
    }
  }

  phoneInput.addEventListener('input', syncPhoneGate);

  /*
   * Pre-filled from customer.phone/default_address.phone for a logged-in
   * shopper — the input event never fires for a value set server-side, so
   * the gate has to be evaluated once up front or a pre-filled number would
   * leave SMS/WhatsApp stuck disabled.
   */
  syncPhoneGate();

  /*
   * Loose on purpose. UK mobiles arrive as 07... or +447...; the endpoint
   * validates properly and returns a 422 with field detail if this misses
   * something — this is a fast local check, not the source of truth.
   */
  function looksLikeUkMobile(value) {
    var digits = value.replace(/[\s()-]/g, '');
    return /^(?:\+447\d{9}|07\d{9})$/.test(digits);
  }

  function post(body) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json()
        .catch(function () { return {}; })
        .then(function (parsed) { return { status: res.status, body: parsed }; });
    });
  }

  /*
   * Explicit render, not the automatic `cf-turnstile` class scan — the
   * container lives inside step 2, which is [hidden] until step 1 passes,
   * and Turnstile can fail to size/initialise a widget whose ancestor is
   * display:none at load time. Rendered once, on first reveal; window.turnstile
   * may not have finished loading yet (the api.js tag is async+defer), so
   * this retries briefly rather than assuming it's ready.
   */
  var turnstileWidgetId = null;
  function renderTurnstileOnce(attemptsLeft) {
    if (turnstileWidgetId !== null) return;
    if (window.turnstile && typeof window.turnstile.render === 'function') {
      turnstileWidgetId = window.turnstile.render('#ConsentTurnstile', { sitekey: turnstileSitekey });
      return;
    }
    if (attemptsLeft <= 0) return;
    window.setTimeout(function () { renderTurnstileOnce(attemptsLeft - 1); }, 150);
  }

  function resetTurnstile() {
    if (turnstileWidgetId !== null && window.turnstile && typeof window.turnstile.reset === 'function') {
      window.turnstile.reset(turnstileWidgetId);
    }
  }

  function getTurnstileToken() {
    if (turnstileWidgetId !== null && window.turnstile && typeof window.turnstile.getResponse === 'function') {
      return window.turnstile.getResponse(turnstileWidgetId) || '';
    }
    return '';
  }

  var emailCheck = root.querySelector('[data-consent-email]');
  var emailValue = ''; // captured from step 1, carried into the single POST from step 2

  /*
   * Step 1 -> step 2. Pure client-side gate, no network call: this is the
   * "email only" first ask the research behind this design calls for.
   * Email consent is required to proceed at all, same reasoning as before —
   * the 10% code is the reward for that consent specifically.
   */
  root.querySelector('[data-consent-continue]').addEventListener('click', function () {
    showEmailError('');

    var email = (root.querySelector('#ConsentEmail').value || '').trim();
    if (!email) { showEmailError('Please enter your email address.'); return; }
    if (!emailCheck.checked) {
      showEmailError('Please tick the box above to get your code by email.');
      return;
    }

    emailValue = email;
    step('phone');
    renderTurnstileOnce(20);
  });

  root.querySelector('[data-consent-submit]').addEventListener('click', function () {
    showError('');

    var phone = (phoneInput.value || '').trim();
    var honeypot = root.querySelector('#ConsentHp');
    var challengeToken = getTurnstileToken();

    if (phone && !looksLikeUkMobile(phone)) {
      showError('That doesn\'t look like a UK mobile number.');
      return;
    }
    if (!challengeToken) {
      showError('Please complete the verification check.');
      return;
    }

    var payload = {
      email: emailValue,
      phone: phone,
      email_consent: true,
      sms_consent: !!smsCheck.checked,
      whatsapp_consent: !!whatsappCheck.checked,
      consent_text_version: consentTextVersion,
      page_url: window.location.href,
      turnstile_token: challengeToken,
      hp: honeypot ? honeypot.value : ''
    };

    busy(true);
    post(payload).then(function (res) {
      busy(false);

      if (res.status === 200 && res.body && res.body.ok) {
        var next = state();
        next.joined = true;
        save(next);
        step('done');
        return;
      }

      if (res.status === 422 && res.body && res.body.error === 'verification') {
        resetTurnstile();
        showError('Sorry — that verification check failed. Please try again.');
        return;
      }

      if (res.status === 422 && res.body && res.body.error === 'validation') {
        var fields = res.body.fields || {};
        var messages = [];
        for (var key in fields) {
          if (Object.prototype.hasOwnProperty.call(fields, key)) messages.push(fields[key]);
        }
        showError(messages.join(' ') || 'Please check the details you entered.');
        resetTurnstile();
        return;
      }

      if (res.status === 429) {
        showError('Please try again later.');
        resetTurnstile();
        return;
      }

      showError('Something went wrong, please try again.');
      resetTurnstile();
    }).catch(function () {
      busy(false);
      showError('Something went wrong, please try again.');
      resetTurnstile();
    });
  });

  /*
   * Never over a shopping window: the minicart, the added-to-basket panel or the
   * Developers pop-out. If one is open when the delay ends, wait until they are all
   * closed, then give the shopper a moment before showing (owner, 2026-10-06).
   *
   * 2026-10-23: also never over anything else the shopper is in the middle of —
   * search (the overlay and the typing dropdown), the menus, the product-page modals,
   * the collection filter drawer, an add-to-cart in flight, an open chat, or any
   * field they are typing into.
   */
  var BUSY_SELECTORS = [
    '#searchPopup.active',             // header / mobile-toolbar search overlay (theme.js theme.Search)
    '.mobile-nav-wrapper.active',      // mobile menu drawer
    '#shade-modal.active',             // bulk shade modal
    '#shade-confirm-modal.active',
    '#pre-order-modal.active',
    '#pdp-sub-popout',                 // shade swatch zoom; only exists while open
    '.mfp-wrap',                       // Magnific popups (enquiry, shipping info); only exists while open
    '.filterbar.active',               // collection / search filter drawer
    'body.loading',                    // add-to-cart in flight
    'shopify-chat[open]',              // Shopify Inbox, new widget
    '#ShopifyChat[is-open="true"]'     // Shopify Inbox, legacy widget
  ].join(',');

  function searchDropdownOpen() {
    // opensearch-autocomplete appends one #os-dropdown per search input, so the id repeats.
    var dropdowns = document.querySelectorAll('[id="os-dropdown"]');
    for (var i = 0; i < dropdowns.length; i++) {
      if (dropdowns[i].style && dropdowns[i].style.display === 'block') return true;
    }
    return false;
  }

  function megaMenuOpen() {
    // Hover/keyboard only, no class to read. try: :focus-within throws on very old browsers.
    try { return !!document.querySelector('#siteNav > li:hover, #siteNav > li:focus-within'); } catch (e) { return false; }
  }

  function typingElsewhere() {
    var el = document.activeElement;
    if (!el || !el.tagName || root.contains(el)) return false;
    var tag = el.tagName.toLowerCase();
    if (tag === 'textarea' || tag === 'select' || el.isContentEditable) return true;
    if (tag !== 'input') return false;
    var type = (el.getAttribute('type') || 'text').toLowerCase();
    return ['checkbox', 'radio', 'button', 'submit', 'reset', 'image', 'file', 'range', 'color', 'hidden'].indexOf(type) < 0;
  }

  function shoppingWindowOpen() {
    if (document.body.classList.contains('dev-popout-open')) return true;
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active')) return true;
    var panel = document.querySelector('cart-notification');
    if (panel && !panel.hidden) return true;
    if (document.querySelector(BUSY_SELECTORS)) return true;
    return searchDropdownOpen() || megaMenuOpen() || typingElsewhere();
  }

  var QUIET_MS = 2000;
  function showWhenQuiet(quietSince) {
    if (state().joined) return;
    var now = Date.now();
    if (shoppingWindowOpen()) {
      window.setTimeout(function () { showWhenQuiet(0); }, 1000);
      return;
    }
    if (quietSince === 0) {
      // Just closed: hold off QUIET_MS in case they open another one straight away.
      window.setTimeout(function () { showWhenQuiet(now); }, QUIET_MS);
      return;
    }
    root.hidden = false;
    step('email');
  }

  window.setTimeout(function () { showWhenQuiet(shoppingWindowOpen() ? 0 : 1); }, delayMs);
})();
